import React, { useState, useCallback, useEffect } from "react";
import { TextField, Autocomplete, SxProps, AutocompleteRenderOptionState, Theme, Popper } from "@mui/material";
import useDebounce from "../../hooks/useDebounce";

interface DebouncedAutoCompleteProps<TOption> {
  value?: TOption | null;
  options: TOption[];
  getOptionsLabel: (option: TOption) => string;
  renderOption?:
    | ((
        props: React.HTMLAttributes<HTMLLIElement>,
        option: TOption,
        state: AutocompleteRenderOptionState
      ) => React.ReactNode)
    | undefined;
  onSelectedOptionChanged: (
    e: React.SyntheticEvent<Element, Event>,
    option: TOption | null | string,
    reason: string
  ) => void;
  textboxLabel: string;
  textboxPlaceholder: string;
  textboxMargin?: "none" | "normal" | "dense";
  onDebouncedTextChanged: (text: string, signal?: AbortSignal) => Promise<void>;
  required?: boolean | undefined;
  error?: boolean | undefined;
  errorMessage?: string;
  disabled?: boolean | undefined;
  freeSolo?: boolean | undefined;
  open?: boolean | undefined;
  onFocus?: () => void | undefined;
  onBlur?: (e?: any | undefined) => void | undefined;
  additionalOnChange?: (e: string) => void | undefined;
  onInputChange?: (r: string) => void | undefined;
  onClick?: () => void | undefined;
  size?: "small" | "medium" | undefined;
  fullWidth?: boolean;
  disableClearable?: boolean;
  disableCloseOnSelect?: boolean;
  preserveInputOnSelect?: boolean;
  sx?: SxProps<Theme> | undefined;
  setText?: any;
  loadingProp?: boolean;
  noOptionsText?: any;
  popperWidth?: any;
  onKeyDown?: (event: any) => void;
  masktype?: "zip";
  InputProps?: any;
  autoHighlight?: boolean;
  isSearch?: boolean;
  isReadOnly?: boolean; //The reason why we need "isReadOnly" in addition to "disabled" is so that the user can still click the close icon in the autocomplete
}

function DebouncedAutoComplete<TOption>(props: DebouncedAutoCompleteProps<TOption>) {
  const {
    renderOption,
    value,
    options,
    getOptionsLabel,
    onSelectedOptionChanged,
    textboxLabel,
    textboxPlaceholder,
    textboxMargin = "none",
    onDebouncedTextChanged,
    required,
    error,
    errorMessage,
    disabled,
    freeSolo,
    open,
    onFocus,
    onBlur,
    additionalOnChange,
    onInputChange,
    onClick,
    fullWidth,
    size,
    disableClearable,
    disableCloseOnSelect,
    preserveInputOnSelect = false,
    sx,
    loadingProp,
    noOptionsText,
    popperWidth,
    onKeyDown,
    masktype,
    InputProps,
    autoHighlight = false,
    isReadOnly = false
  } = props;

  const [textboxText, setTexboxText] = useState("");
  const [loading, setLoading] = useState(false);
  const debouncedText = useDebounce(textboxText, 700);
  const abortControllerRef = React.useRef<AbortController | null>(null);

  const PopperMy = React.useCallback((props: any) => {
    ////This was causing an in issue when poper widths weren't passed in as props. This probably could be done more eloquently
    if (popperWidth) {
      return <Popper {...props} style={{ width: popperWidth }} placement="bottom-start" />;
    }

    return <Popper {...props} placement="bottom-start" />;
  }, []);

  useEffect(() => {
    setLoading(true);
    if (!debouncedText) {
      setLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    onDebouncedTextChanged(textboxText, abortController.signal)
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.error("Error in debounced autocomplete:", error);
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [debouncedText]);

  return (
    <Autocomplete
      onClick={onClick}
      disableClearable={disableClearable}
      disableCloseOnSelect={disableCloseOnSelect}
      size={size}
      autoHighlight={autoHighlight}
      fullWidth={fullWidth}
      disabled={disabled}
      freeSolo={freeSolo}
      value={value || ""}
      inputValue={preserveInputOnSelect ? textboxText : undefined}
      open={open}
      onKeyDown={onKeyDown}
      options={loading ? [] : options || []}
      PopperComponent={PopperMy}
      getOptionLabel={(option) => getOptionsLabel(option as TOption)}
      renderOption={renderOption}
      onChange={(e, value, reason) => {
        onSelectedOptionChanged(e, value, reason);
      }}
      filterOptions={(o) => o} /* Don't filter the drop down options based on the text typed into the textbox*/
      onInputChange={(e, v, r) => {
        if (onInputChange) {
          onInputChange(v);
        }

        if (preserveInputOnSelect && (r === "input" || r === "clear")) {
          additionalOnChange ? additionalOnChange(v) : null;
          setTexboxText(v);
          props.setText && props.setText(v);
        }
      }}
      renderInput={(params) => (
        <TextField
          margin={textboxMargin}
          required={required}
          error={error}
          value={textboxText}
          {...params}
          InputProps={{ ...params.InputProps, ...InputProps }}
          inputProps={{
            ...params.inputProps,
            name: "password",
            autoComplete: "off",
            type: "text",
            readOnly: isReadOnly,
            sx: {
              ...(isReadOnly ? { cursor: "not-allowed", color: "#919EAB" } : {})
            }
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "&:hover .MuiOutlinedInput-notchedOutline": {
                ...(isReadOnly ? { border: "1px solid #e0e0e0" } : {})
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                ...(isReadOnly ? { border: "1px solid #e0e0e0" } : {})
              }
            }
          }}
          label={textboxLabel}
          placeholder={textboxPlaceholder}
          onChange={(e) => {
            if (!preserveInputOnSelect) {
              additionalOnChange ? additionalOnChange(e.target.value) : null;
              setTexboxText(e.target.value);
              props.setText && props.setText(e.target.value);
            }
          }}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      )}
      loading={loadingProp === undefined ? loading : loadingProp}
      sx={sx}
      noOptionsText={noOptionsText}
    />
  );
}

export default DebouncedAutoComplete;
